import { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { FaFlag } from 'react-icons/fa';
import { useFundRequestContext } from '~/context/FundRequestProvider';
import {
    rejectACH,
    requestACHInfo,
    setStatusNeedsAdditionalInfo,
} from '~/services/fund-requests';

const RequestACHInfoModal = ({
    modalOpen,
    setModalOpen,
}: {
    modalOpen: boolean;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    const { fundRequest, fundRequestDispatch } = useFundRequestContext();
    const { register, handleSubmit } = useForm({
        defaultValues: {
            note: '',
        },
    });
    const [saving, setSaving] = useState(false);

    const onSubmit = async (data: any) => {
        const { note } = data;
        
        // Validate note is not empty
        if (!note || note.trim() === '') {
            toast.error('Please provide a message for the client');
            return;
        }
        
        setSaving(true);
        toast.promise(
            async () => {
                const updatedFundRequest = await requestACHInfo(
                    fundRequest.id,
                    note
                );
                fundRequestDispatch({
                    type: 'set_fund_request',
                    payload: updatedFundRequest,
                });
                setSaving(false);
                setModalOpen(false);
            },
            {
                loading: 'Submitting ACH request...',
                success: 'ACH request sent',
                error: (err) => {
                    setSaving(false);
                    return `Error requesting ACH: ${err}`;
                },
            }
        );
    };

    return (
        <>
            {modalOpen && (
                <div
                    className='fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-75'
                    onClick={() => setModalOpen(false)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className='relative w-full max-w-xl rounded-lg bg-white shadow-lg'
                    >
                        <div className='p-7'>
                            <h2 className='mb-4 text-3xl font-semibold'>
                                ACH Info Request
                            </h2>
                            <p className='mb-5'>
                                You are changing the status of this Fund Request
                                to Needs Additional Info. Please provide some
                                information for the Client in order to help them
                                make the necessary changes.
                            </p>

                            <h3 className='mb-5 text-[18px]/[22px] font-bold uppercase'>
                                Flagged Issues
                            </h3>
                            <div className='flex items-center gap-3 font-bold text-danger'>
                                <FaFlag color='danger' /> ACH Info Needed
                            </div>

                            <div className='mt-6'>
                                <label className='block font-bold'>
                                    Message
                                    <span className='text-danger'>*</span>
                                </label>
                                <textarea
                                    className='w-full resize-none rounded border p-2'
                                    placeholder='Text'
                                    rows={6}
                                    {...register('note')}
                                />
                            </div>
                            <div className='flex gap-[16px]'>
                                <button
                                    onClick={() => setModalOpen(false)}
                                    type='button'
                                    className='mt-[30px]  rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8]'
                                >
                                    Cancel
                                </button>
                                <button
                                    type='button'
                                    className={`mt-[30px] rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black  transition-colors duration-500 ${
                                        saving
                                            ? 'cursor-not-allowed opacity-50'
                                            : 'hover:border-[#E6F7F8] hover:bg-[#E6F7F8]'
                                    }`}
                                    onClick={handleSubmit(onSubmit)}
                                    disabled={saving}
                                >
                                    {saving ? 'Saving...' : 'Save & Send'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default RequestACHInfoModal;
